import { StartChatInitialData } from '@wix/wix-chatbot-common/browser';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { experiments } from '../../experiments';
import { renderModel } from '../../util/renderModel';
import { chatActions } from '../reducers/chat';
import { selectStartingNodeId } from '../selectors/chat';
import { selectDealerOfferParams } from '../selectors/meta';
import { useGetInitialChatbotState } from './useGetInitialChatbotState';
import { useResetChatbotState } from './useResetChatbotState';

const newWidgetExperience = experiments.is('specs.helpwidget.newWidgetExperience', 'true');

export const useStartChatListener = () => {
  const dispatch = useDispatch();
  const { getInitialChatbotState, isParamsDifferent } = useGetInitialChatbotState();
  const resetChatbotState = useResetChatbotState();
  const chatStartingNodeId = useSelector(selectStartingNodeId);
  const dealerOfferParams = useSelector(selectDealerOfferParams);
  const isChatbotDisabled = renderModel.isChatbotDisabled();

  const startChatWithInitialDataFunctionRef = useRef(async (params?: Map<string, string>) => {
    if (isChatbotDisabled) {
      return;
    }

    dispatch(chatActions.setChatbotDataLoading(true));

    const { initialState } = await getInitialChatbotState(params);
    resetChatbotState(initialState as StartChatInitialData);

    dispatch(chatActions.setChatbotDataLoading(false));
  });

  useEffect(() => {
    // nodeId can be empty string(to start chat from root node)
    if (chatStartingNodeId !== undefined) {
      if (newWidgetExperience) {
        dispatch(
          chatActions.setStartChatParameters({ nodeId: chatStartingNodeId, shouldStartNewChat: 'true' }),
        );
      } else {
        const params = new Map([
          ['nodeId', chatStartingNodeId],
          ['shouldStartNewChat', 'true'],
        ]);
        void startChatWithInitialDataFunctionRef.current(params);
      }
    }
  }, [chatStartingNodeId]);

  useEffect(() => {
    if (dealerOfferParams) {
      if (newWidgetExperience) {
        dispatch(chatActions.setStartChatParameters(Object.fromEntries(dealerOfferParams)));
        return;
      }

      if (isParamsDifferent(dealerOfferParams)) {
        void startChatWithInitialDataFunctionRef.current(dealerOfferParams);
      }
    }
  }, [isParamsDifferent, dealerOfferParams]);
};
